import {
  Alert,
  Box,
  Button,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from "@mui/material";
import Loader from "components/Loader";
import RedirectBackButton from "components/RedirectBackButton";
import { format } from "date-fns";
import { FORM_ERROR } from "final-form";
import arrayMutators from "final-form-arrays";
import useGlobalContext from "hooks/globalData";
import { useStrictQuery } from "hooks/useStrictQuery";
import useTitle from "hooks/useTitle";
import useTrashablePromises from "hooks/useTrashablePromises";
import ManualGradingAnswer from "pages/GradeUserExamsPage/ManualGradingAnswer";
import { useMemo } from "react";
import { withTypes } from "react-final-form";
import { FieldArray } from "react-final-form-arrays";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery, useQueryClient } from "react-query";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import {
  ListRequestOfUserSkuActivitiesFilter,
  SubmitManualGradingExamRequest,
  UserAnswerGrade,
  UserSkuActivitiesFilter,
} from "services/KnowfullyAdminClient";
import { ActivityEvents, ActivityTypes } from "types/common-types";
import { convertToZonedDate } from "utils/dateUtils";
import translateError from "utils/translateError";

type ManualGradingExamFormValues = {
  userAnswerGrades: UserAnswerGrade[];
};

const ManualGradingExamDetails = () => {
  const { id } = useParams<{ id: string }>();
  const {
    gradeExamsUrl,
    userEvaluationService,
    userSkuService,
  } = useGlobalContext();
  const { formatMessage: t } = useIntl();
  const navigate = useNavigate();
  const { Form } = withTypes<ManualGradingExamFormValues>();
  const queryClient = useQueryClient();
  const { registerPromise } = useTrashablePromises();

  const { isLoading, isError, data, isFetched } = useQuery(
    ["manualGradingExam", id],
    () => userEvaluationService.getManualGradingExamDetails(id!),
    {
      enabled: !!id,
    }
  );

  const init = useMemo(
    () => ({
      userAnswerGrades: data?.data?.userAnswers?.map(
        (a) => ({ id: a.id } as UserAnswerGrade)
      ),
    }),
    [data]
  );

  const exam = data?.data!;
  const name = exam?.userName || exam?.email || "";
  useTitle(`Grade ${name} exam`);

  const request = new ListRequestOfUserSkuActivitiesFilter({
    pageNumber: 0,
    pageSize: -1,
    sortField: "activityDate",
    sortDescending: true,
    parameters: new UserSkuActivitiesFilter({
      userSkuId: exam?.userSkuId,
    }),
  });

  const userSkuActivitiesResponse = useStrictQuery(
    ["userSkuService.getUserSkuActivities", request],
    () => userSkuService.getUserSkuActivities(request),
    {
      enabled: !!exam?.userSkuId,
    }
  );

  const userSkuActivities = userSkuActivitiesResponse.data?.data || [];

  const examAttemptActivity = userSkuActivities.find(
    (activity) =>
      activity.activityTypeName === ActivityTypes.ExamAttempt &&
      activity.activityEventName === ActivityEvents.GradingPending
  );

  if (!id) return <Navigate to={gradeExamsUrl()} replace />;
  if (isLoading) return <Loader />;
  if (isError) {
    return (
      <Box mt={4}>
        <Alert severity="error">
          <T id="evaluationLoadError" />
        </Alert>
      </Box>
    );
  }
  if (isFetched && !exam) return <Navigate to={gradeExamsUrl()} replace />;
  const rows = [
    {
      title: t({ id: "userNameLabel" }),
      component: <span>{exam.userName}</span>,
    },
    {
      title: t({ id: "emailLabel" }),
      component: <span>{exam.email}</span>,
    },
    {
      title: t({ id: "evaluationDateLabel" }),
      component: examAttemptActivity?.activityDate && (
        <span>
          {format(convertToZonedDate(examAttemptActivity.activityDate)!, "P")}
        </span>
      ),
    },
    {
      title: t({ id: "courseLabel" }),
      component: <span>{exam.courseTitle}</span>,
    },
    {
      title: t({ id: "scoreLabel" }),
      component: <T id="pendingLabel" />,
    },
  ];

  const validateForm = async (values: ManualGradingExamFormValues) => {
    const errors: any = { userAnswerGrades: [] };

    values.userAnswerGrades.forEach((answer, index) => {
      let fieldErrors: any = {};

      if (answer.isCorrect == null) {
        fieldErrors.isCorrect = t({ id: "requiredLabel" });
      }

      if (answer.isCorrect === false && !answer.feedback) {
        fieldErrors.feedback = t({ id: "feedbackRequired" });
      }

      errors.userAnswerGrades[index] = fieldErrors;
    });

    return errors;
  };

  const onSubmit = async (values: ManualGradingExamFormValues) => {
    try {
      const response: Response = await registerPromise(
        userEvaluationService.submitManualGradingExam(
          id,
          SubmitManualGradingExamRequest.fromJS({
            ...exam,
            answerGrades: values.userAnswerGrades,
          })
        )
      );

      queryClient.invalidateQueries("manualGradingExams");
      queryClient.invalidateQueries(["manualGradingExam", id]);

      navigate(gradeExamsUrl());
      return response;
    } catch (err) {
      console.log(err);
      return { [FORM_ERROR]: translateError(err, t) };
    }
  };

  return (
    <>
      <Box my={2}>
        <RedirectBackButton
          redirectRoute={gradeExamsUrl()}
          labelKey="backToListButtonLabel"
        />
      </Box>
      <Typography variant="h4">{exam.name}</Typography>
      <Box my={2}>
        <TableContainer>
          <Table>
            <TableBody>
              {rows.map(({ title, component }, i) => (
                <TableRow key={i}>
                  <TableCell component="th" scope="row">
                    <b>{title}</b>
                  </TableCell>
                  <TableCell>{component}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      <Box my={2}>
        <Typography variant="h5">
          {t({ id: "evaluationFormQuestionsLabel" })}
        </Typography>
        <Form
          onSubmit={onSubmit}
          validate={validateForm}
          initialValues={init}
          mutators={{ ...arrayMutators }}
          render={({ handleSubmit, submitting, submitError }) => (
            <form onSubmit={handleSubmit}>
              <FieldArray name="userAnswerGrades">
                {({ fields }) => (
                  <>
                    {fields.map((name, index) => {
                      const userAnswer = exam.userAnswers![index];
                      return (
                        <ManualGradingAnswer
                          key={userAnswer.id}
                          fieldName={name}
                          disabled={submitting}
                          userAnswer={userAnswer}
                        />
                      );
                    })}
                  </>
                )}
              </FieldArray>
              {submitError && <Alert severity="error">{submitError}</Alert>}
              <DialogActions>
                <Button
                  disabled={submitting}
                  onClick={() => navigate(gradeExamsUrl())}
                  data-cy="cancelButton"
                >
                  {t({ id: "cancelButton" })}
                </Button>

                <Button
                  type="submit"
                  color="primary"
                  variant="contained"
                  disabled={submitting}
                  data-cy="submitGradingButton"
                >
                  <T id="submitGradingButton" />
                </Button>
              </DialogActions>
            </form>
          )}
        />
      </Box>
    </>
  );
};

export default ManualGradingExamDetails;
